import React, { CSSProperties, useRef, useState, type ReactNode, type RefObject } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { testId as getTestId } from '@jwp/ott-common/src/utils/common';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import { useTheme } from '../../../../../ThemeContext';

import HelperText from '../HelperText/HelperText';

import styles from './TextField.module.scss';

type InputProps = Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'id' | 'ref' | 'className' | 'name'>;
type TextAreaProps = Omit<React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>, 'id' | 'ref' | 'className' | 'name'>;

type InputOrTextAreaProps =
  | ({ multiline?: never; inputRef?: RefObject<HTMLInputElement>; textAreaRef?: never } & InputProps)
  | ({ multiline: true; inputRef?: never; textAreaRef?: RefObject<HTMLTextAreaElement> } & TextAreaProps);

type Props = {
  name: string;
  className?: string;
  label?: ReactNode;
  helperText?: React.ReactNode;
  leftControl?: React.ReactNode;
  rightControl?: React.ReactNode;
  error?: boolean;
  editing?: boolean;
  testId?: string;
  multiline?: boolean;
  customStyle?: CSSProperties;
  borderedReadOnlyMood?: boolean;
  otpMode?: boolean;
  showOptionalLabel?: boolean;
} & InputOrTextAreaProps;

const TextField: React.FC<Props> = ({
  name,
  className,
  label,
  error,
  helperText,
  leftControl,
  rightControl,
  editing = true,
  testId,
  inputRef,
  textAreaRef,
  multiline,
  borderedReadOnlyMood,
  customStyle,
  otpMode = false,
  showOptionalLabel = true,
  ...inputProps
}: Props) => {
  const id = useOpaqueId('text-field', name);
  const helperTextId = useOpaqueId('helper_text', name);
  const length = inputProps.maxLength || 6;
  const [otpValue, setOtpValue] = useState(Array(length).fill(''));
  const { t } = useTranslation('common');
  const { universalStyles } = useTheme();

  const isInputOrTextArea = (item: unknown): item is InputOrTextAreaProps => !!item && typeof item === 'object';
  const isTextArea = (item: unknown): item is TextAreaProps => isInputOrTextArea(item) && !!multiline;

  const textFieldClassName = classNames(
    styles.textField,
    {
      [styles.error]: error,
      [styles.disabled]: inputProps.disabled,
      [styles.leftControl]: !!leftControl,
      [styles.rightControl]: !!rightControl,
      [styles.borderedReadOnlyMoodPadding]: borderedReadOnlyMood,
    },
    className,
  );

  const textFieldStyle = {
    ...(error ? { ...universalStyles.errorBorder } : {}),
  };


  const otpRefs = Array.from({ length }, () => useRef<HTMLInputElement>(null));

  const renderOtpInput = () => (
    <div >
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          ref={otpRefs[index]}
          name={`QrCode_${index}`}
          className={`${styles.otpBox} ${otpValue[index] ? styles.filled : ""}`}
          value={otpValue[index]}
          onChange={(e) => {
            const val = e.target.value;
            const updated = [...otpValue];
            updated[index] = val;
            setOtpValue(updated);

            if (val && index < length - 1) {
              otpRefs[index + 1].current?.focus();
            }

            inputProps.onChange?.({
              ...e,
              target: { ...e.target, name: 'QrCode', value: updated.join('') },
            });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
              otpRefs[index - 1].current?.focus();
            }
          }}
        />
      ))}
    </div>
  );



  const renderInput = () => {
    const { required, disabled, value, ...otherInputProps } = inputProps;

    // Default to 'text' if 'type' property is absent, which occurs in textareas.
    const inputType = 'type' in otherInputProps ? otherInputProps.type : 'text';

    const ariaAttributes = {
      'aria-invalid': Boolean(error && !value),
      'aria-describedby': helperTextId,
    } as const;

    const commonProps = {
      id,
      name,
      value,
      disabled,
      className: styles.input,
      readOnly: !editing,
      required: !!required,
      ...ariaAttributes,
      ...otherInputProps,
    };

    const handleTextAreaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      otherInputProps.onKeyDown?.(e as any);
    };

    if (otpMode) return renderOtpInput();

    return isTextArea(inputProps) ? (
      <textarea {...(commonProps as TextAreaProps)} rows={4} ref={textAreaRef} onKeyDown={handleTextAreaKeyDown} />
    ) : (
      <input {...(commonProps as InputProps)} type={inputType} ref={inputRef} />
    );
  };

  if (otpMode) {
    return (
      <div className={styles.otpContainer}>
        {renderInput()}
        <HelperText id={helperTextId} error={error}>
          {helperText}
        </HelperText>
      </div>
    );
  }

  return (
    <div className={textFieldClassName} data-testid={getTestId(testId)}>
      <label htmlFor={id} className={styles.label} style={customStyle}>
        {label}
        {inputProps.required ? <span className={styles.required} style={universalStyles.errorColor}>*</span> : null}
        {!inputProps.required && editing && showOptionalLabel ? <span>{t('optional')}</span> : null}
      </label>
      {editing ? (
        <div className={`${styles.container} ${otpMode ? styles['otpEnabled'] : ''} `} style={textFieldStyle}>
          {leftControl ? <div className={styles.control}>{leftControl}</div> : null}
          {renderInput()}
          {rightControl ? <div className={styles.control}>{rightControl}</div> : null}
        </div>
      ) : borderedReadOnlyMood ? (
        <div className={`${styles.container1} ${otpMode ? styles['otpEnabled'] : ''}`}>
          {leftControl ? <div className={styles.control}>{leftControl}</div> : null}
          {renderInput()}
          {rightControl ? <div className={styles.control}>{rightControl}</div> : null}
        </div>
      ) : (
        renderInput()
      )}
      <HelperText id={helperTextId} error={error}>
        {helperText}
      </HelperText>
    </div>
  );
};

export default TextField;
